import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Divider } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useRetrievePlayersByTeamId, useRetrieveTeamPreferences, } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { DeleteDialog } from "../utils/DeleteDialog";
import { FormActions } from "../utils/FormActions";
import { TeamChooser } from "./TeamChooser";
export const IntraSquadMatchForm = ({ team, onSubmit, intraSquadMatch, }) => {
    const useFormProps = useForm({
        defaultValues: {
            kickOff: intraSquadMatch?.kickOff,
            teamAPlayers: intraSquadMatch?.teamAPlayers.map((p) => p.id) || [],
            teamBPlayers: intraSquadMatch?.teamBPlayers.map((p) => p.id) || [],
            team: team.id,
        },
    });
    const { control, handleSubmit, formState: { errors }, setValue, } = useFormProps;
    const { data: players, error: playersError, isLoading: playersIsLoading, } = useRetrievePlayersByTeamId({ teamId: team.id });
    const { isLoading: useRetrieveTeamPreferenceIsLoading, error: useRetrieveTeamPreferenceError, data: teamPreference, } = useRetrieveTeamPreferences({ teamId: team.id });
    const [teamSelection, _setTeamSelection] = React.useState({
        teams: [],
        bench: [],
    });
    const setTeamSelection = (newSelection) => {
        _setTeamSelection(newSelection);
        const [teamA, teamB] = newSelection.teams;
        setValue("teamAPlayers", teamA.players.map((p) => p.id));
        setValue("teamBPlayers", teamB.players.map((p) => p.id));
    };
    // A departed player dragged to the bench, awaiting a removal confirmation.
    const [pendingRemoval, setPendingRemoval] = React.useState(null);
    // Held separately so the name survives the dialog's close animation on cancel.
    const [removalName, setRemovalName] = React.useState("");
    const requestRemoval = (player) => {
        setRemovalName(player.name ?? "This player");
        setPendingRemoval(player);
    };
    const confirmRemoveDeparted = () => {
        if (pendingRemoval?.id != null) {
            const removedId = pendingRemoval.id;
            setTeamSelection({
                ...teamSelection,
                teams: teamSelection.teams.map((t) => ({
                    ...t,
                    players: t.players.filter((p) => p.id !== removedId),
                })),
            });
        }
        setPendingRemoval(null);
    };
    React.useEffect(() => {
        if (!players || !teamPreference)
            return;
        const getRosterPlayerById = (id) => players.find((p) => p.id === id);
        const selectedIds = new Set([
            ...(intraSquadMatch?.teamAPlayers.map((p) => p.id) || []),
            ...(intraSquadMatch?.teamBPlayers.map((p) => p.id) || []),
        ]);
        // Roster members render as full players; anyone who has left the team stays
        // as a summary (greyed + warning pill), removable only via confirmation.
        const resolveLineup = (lineup) => lineup.map((mp) => getRosterPlayerById(mp.id) ?? mp);
        setTeamSelection({
            teams: [
                {
                    name: teamPreference.teamAName,
                    players: resolveLineup(intraSquadMatch?.teamAPlayers || []),
                    primaryColour: undefined,
                },
                {
                    name: teamPreference.teamBName,
                    players: resolveLineup(intraSquadMatch?.teamBPlayers || []),
                    primaryColour: undefined,
                },
            ],
            bench: players.filter((p) => p.isActive !== false && !selectedIds.has(p.id)),
        });
    }, [players, teamPreference, intraSquadMatch]);
    if (useRetrieveTeamPreferenceIsLoading || playersIsLoading) {
        return _jsx(Loading, {});
    }
    if (!team ||
        useRetrieveTeamPreferenceError ||
        !teamPreference ||
        playersError ||
        !players) {
        return _jsx(ErrorView, { error: useRetrieveTeamPreferenceError });
    }
    return (_jsx(FormProvider, { ...useFormProps, children: _jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, sx: { mt: 1 }, children: [_jsx(Controller, { name: "kickOff", control: control, rules: { required: "Kick-Off is required" }, render: ({ field }) => (_jsx(DateTimePicker, { label: "Kick-Off", ...field, value: field.value ? dayjs(field.value) : null, onChange: (newValue) => field.onChange(newValue), sx: { width: "100%" }, slotProps: {
                            textField: {
                                error: !!errors.kickOff,
                                helperText: errors.kickOff ? errors.kickOff.message : "",
                            },
                        } })) }), _jsx(Divider, { sx: {
                        margin: "20px",
                    } }), _jsx(TeamChooser, { teamSelection: teamSelection, setTeamSelection: setTeamSelection, canEditTeamNames: false, onRemoveDeparted: requestRemoval }), _jsx(DeleteDialog, { open: pendingRemoval !== null, title: `Remove ${removalName || "player"}?`, description: `${removalName} has left the team, so they can't be added back once removed from this match.`, confirmText: "Remove", onCancel: () => setPendingRemoval(null), onConfirm: confirmRemoveDeparted }), _jsx(FormActions, { submitLabel: intraSquadMatch === undefined ? "Create" : "Update" })] }) }));
};
